// Punnets grouped by SHIFT, not by calendar date.
//
// WHAT PRECIOUS ASKED FOR (Anchor, 2026-09-16). She checks that the punnets
// captured per person per shift are right. When packing runs past midnight she
// cannot: a tally carries `work_date`, the capture station stamps it with the
// SAST calendar day, and nothing rolls a 00:10 capture back onto the shift it
// belongs to. One night therefore reads as two half-days.
//
// ⚠️ The clock does NOT split that way. A cross-midnight shift stays ONE
// labour_log row dated by the day it STARTED (mig 20260703123515), with the
// post-midnight tail attributed for Sunday/public-holiday banding. So hours
// reconcile per shift while punnets reconcile per calendar day, and her two
// screens cannot be made to agree.
//
// ⚠️ THIS FILE CHANGES NOTHING ABOUT PAY. Piece tiers accumulate
// `PARTITION BY tenant_id, worker_number, work_date, rate_id`
// (20260730054324), so a split night still bands as two days and still pays as
// two days. Re-dating the tallies at capture is the fix for that; it moves
// money, needs a boundary everyone agrees on and a decision about days already
// paid, and it is deliberately NOT this change. What this gives her is the
// count she is actually asking for, plus the number that says how much of the
// shift landed on the next day's pay.
//
// WHERE THE TIMES COME FROM. `packout_capture_drops` (mig 20261021020000) is
// one row per tap at the station, with a server-stamped `logged_at`. That is a
// real capture time. The other three feeds are not:
//
//   source    per-drop times?  what logged_at means
//   CAPTURE   yes              when the packer's units were tapped in
//   UPLOAD    no               when the line-system file was imported
//   SCANNER   no               when the push arrived
//   MANUAL    no               when the office typed the row
//
// So only CAPTURE rows can be re-bucketed by time. The other three are reported
// as they stand, on their own work_date, and the screen says so rather than
// implying a precision the data does not have.

import { sastYMD } from '@/lib/dates'
import { compareNatural } from '@/lib/naturalSort'

/** One tap at the capture station. */
export interface PunnetDrop {
  worker_number: string
  /** From the detail view's join; the register still falls back to the roster. */
  worker_name?:  string | null
  unit_type:     string
  units:         number
  /** Server-stamped capture time (timestamptz ISO). */
  logged_at:     string
  line_ref:      string | null
}

export interface ShiftPunnetRow {
  worker_number: string
  worker_name:   string | null
  /** The shift's own day: the date the shift is counted under. */
  shift_day:     string
  /** Units per unit_type, e.g. { PUNNET: 812 }. */
  byUnit:        Record<string, number>
  units:         number
  drops:         number
  firstAt:       string
  lastAt:        string
  /**
   * Units captured after midnight, i.e. on a calendar date later than the
   * shift day. THE NUMBER SHE IS AFTER: these are the ones pay counts as a
   * separate day, and the reason her per-shift total never matched.
   */
  afterMidnightUnits: number
}

/**
 * The hour a packhouse day starts. Anything captured before it belongs to the
 * previous day's shift. 0 = calendar days, i.e. what the system does today.
 */
export const DEFAULT_SHIFT_CUTOFF_HOUR = 6

/** SAST is UTC+2 all year, so subtracting the cutoff can never land on a DST seam. */
const HOUR_MS = 3_600_000

/**
 * Which shift day a capture belongs to. A drop at 00:10 with a 06:00 cutoff is
 * counted under the previous day; the same drop with cutoff 0 keeps its own
 * date, which is exactly what the pay side does today.
 */
export function shiftDayOf(loggedAt: string, cutoffHour: number): string {
  const t = new Date(loggedAt).getTime()
  if (Number.isNaN(t)) return ''
  return sastYMD(new Date(t - cutoffHour * HOUR_MS))
}

/** 2dp, the scale packout_tallies.units is stored at — float sums drift. */
const round2 = (n: number): number => Math.round(n * 100) / 100

/**
 * Group the station's drops into one row per worker per shift.
 *
 * Rows come back sorted by shift day (newest first), then by the worker's
 * number, so a printed page reads the way the register does.
 */
export function groupDropsByShift(drops: PunnetDrop[], cutoffHour: number): ShiftPunnetRow[] {
  const by = new Map<string, ShiftPunnetRow>()
  for (const d of drops) {
    const shiftDay = shiftDayOf(d.logged_at, cutoffHour)
    if (!shiftDay || !d.worker_number) continue
    // A NUL separator can never occur in a worker number. Written as an escape:
    // a raw NUL byte in source made git treat this file as binary.
    const key = `${d.worker_number}\u0000${shiftDay}`
    const units = Number(d.units) || 0
    const row = by.get(key) ?? {
      worker_number: d.worker_number, worker_name: d.worker_name ?? null,
      shift_day: shiftDay, byUnit: {}, units: 0,
      drops: 0, firstAt: d.logged_at, lastAt: d.logged_at, afterMidnightUnits: 0,
    }
    row.worker_name = row.worker_name ?? d.worker_name ?? null
    row.byUnit[d.unit_type] = round2((row.byUnit[d.unit_type] ?? 0) + units)
    row.units = round2(row.units + units)
    row.drops += 1
    if (d.logged_at < row.firstAt) row.firstAt = d.logged_at
    if (d.logged_at > row.lastAt)  row.lastAt  = d.logged_at
    // The calendar date pay will count this drop under, which is the tally's
    // work_date: the station stamps the SAST day it was tapped.
    if (sastYMD(new Date(d.logged_at)) !== shiftDay) {
      row.afterMidnightUnits = round2(row.afterMidnightUnits + units)
    }
    by.set(key, row)
  }
  return [...by.values()].sort((a, b) =>
    b.shift_day.localeCompare(a.shift_day) || compareNatural(a.worker_number, b.worker_number))
}

/** Does this shift straddle midnight? Drives the "1 shift split over 2 days" note. */
export const isSplitShift = (r: ShiftPunnetRow): boolean => r.afterMidnightUnits > 0

export interface ShiftTotals {
  units:              number
  afterMidnightUnits: number
  workers:            number
  splitShifts:        number
}

/** The footer, and the only figures worth reading off a phone. */
export function totalsFor(rows: ShiftPunnetRow[]): ShiftTotals {
  return {
    units:              round2(rows.reduce((n, r) => n + r.units, 0)),
    afterMidnightUnits: round2(rows.reduce((n, r) => n + r.afterMidnightUnits, 0)),
    workers:            new Set(rows.map(r => r.worker_number)).size,
    splitShifts:        rows.filter(isSplitShift).length,
  }
}

/**
 * What the screen says under the total. It names the split in the words she
 * used — a shift, not a date — and never implies the pay has moved.
 */
export function splitNote(t: ShiftTotals): string | null {
  if (t.afterMidnightUnits <= 0) return null
  const s = t.splitShifts === 1 ? 'shift' : 'shifts'
  return `${t.afterMidnightUnits.toLocaleString()} of these punnets were captured after midnight, on ${t.splitShifts} ${s}. `
    + `They are counted here under the shift that was running, and on the pay run under the next day — `
    + `that difference is why a shift total and a day total disagree.`
}

/**
 * The Shift table as rows for CSV / Excel — the SAME columns the screen shows,
 * in the same order, and no rand (see the file header for why). `fmtTime`
 * renders a capture instant as the SAST wall-clock the screen shows and
 * `nameOf` is the register's roster resolver — the same fallback the table
 * uses when the drop view carries no name — both passed in so this module
 * stays free of the labour module's React helpers.
 */
export function shiftAoa(
  rows: ShiftPunnetRow[],
  words: { Plural: string },
  fmtTime: (iso: string) => string,
  nameOf: (workerNumber: string) => string = n => n,
  /**
   * The day-start hour the rows were grouped by. When given, the sheet ends
   * with two lines saying so — a spreadsheet carries none of the screen's
   * context, and "which night does 03:00 belong to?" is the first question a
   * reader of a by-shift file asks.
   */
  cutoffHour?: number,
): string[][] {
  const aoa: string[][] = [[
    'Shift day', 'Packer', 'Number', 'First', 'Last', words.Plural, 'After midnight', 'Taps',
  ]]
  for (const r of rows) {
    aoa.push([
      r.shift_day, r.worker_name ?? nameOf(r.worker_number), r.worker_number,
      fmtTime(r.firstAt), fmtTime(r.lastAt),
      String(r.units), String(r.afterMidnightUnits), String(r.drops),
    ])
  }
  if (cutoffHour !== undefined) {
    const start = cutoffHour === 0
      ? 'midnight (calendar days)'
      : `${String(cutoffHour).padStart(2, '0')}:00`
    aoa.push([])
    aoa.push([`Grouped by shift; the packhouse day starts at ${start}.`])
    aoa.push(['The After midnight column is counted by the pay run on the NEXT day — that is why a shift total and a day total differ.'])
  }
  return aoa
}

/**
 * The window to ask the database for. A shift day of N holds drops from N at
 * the cutoff through N+1 at the cutoff, so the read has to reach a day past the
 * last day on screen or the night of the last day comes back half-empty.
 */
export function dropWindow(fromDay: string, toDay: string, cutoffHour: number): { fromIso: string; toIso: string } {
  const at = (ymd: string, addDays: number): string =>
    new Date(new Date(`${ymd}T00:00:00+02:00`).getTime() + addDays * 24 * HOUR_MS + cutoffHour * HOUR_MS).toISOString()
  return { fromIso: at(fromDay, 0), toIso: at(toDay, 1) }
}
